/**
 * ReplicationSerializer - serializes and deserializes actor replicated properties.
 *
 * ## Wire format
 *
 * Each actor replication payload (`Uint8Array`) is laid out as:
 * ```
 * [actor bitmask: ceil(N/8) bytes]
 * [values for each set bit in property-index order]
 * [uint8 componentCount]
 *   [uint8 subId] [component bitmask: ceil(M/8) bytes] [component values...]
 *   ...
 * ```
 *
 * Value encoding per `ReplicationNetType` (all multi-byte values are little-endian):
 * - `bool`                                 : 1 bit
 * - `uint`/`int`                           : zigzag varsint delta (`new − old`; base 0 on first send)
 * - `float` (no quantization)              : 4 bytes absolute float32
 * - `float` + `{ mode: 'float16' }`        : varsint delta of float16 bit pattern (uint16)
 * - `float` + `{ mode: 'float32' }`        : varsint delta of monotonic float32 mapping (uint32)
 * - `float` + `{ mode: 'float64' }`        : 8 bytes absolute float64
 * - `float` + `{ mode: 'scale' | 'range' }`: varsint delta of quantized integer
 * - `string`                               : varuint byte length + UTF-8 bytes
 * - `actorRef`                             : 2-bit tag (0=null, 1=8-byte, 2=16-byte) + raw hex bytes
 * - `vector2`                              : 2-bit component mask + changed components
 * - `vector3`/`euler`                      : 3-bit component mask + changed components
 * - `vector4`                              : 4-bit component mask + changed components
 * - `quaternion` (no quantization)         : 4-bit component mask + changed float32 components
 * - `quaternion` + `{ mode: 'drop-w' }`    : 3 × varsint qi deltas (x, y, z; w always dropped)
 * - vector components (int/uint)           : varsint delta; float components: absolute float32
 * - `array`                                : 1-bit mode (0=full, 1=delta); full: varuint count + elements; delta: bitmask + changed elements
 * - `struct`                               : [bitmask][values] recursively
 * - `class`                                : if polymorphic: [uint32 hash][bitmask][values], else [bitmask][values]
 *
 * Nullable properties are prefixed with a 1-bit presence flag (0 = null, 1 = value follows).
 *
 * Per-property and per-object encoding details live in {@link ObjectSerializer}.
 */
import 'reflect-metadata';

import { getDefaultObject } from '../utils/serialization/cdo.js';

import {
  decodeObjectFromBinary,
  serializeObjectAndEncode,
  serializeObjectToState,
} from './ObjectSerializer.js';
import {
  BinaryReader,
  BinaryWriter,
} from './ReplicationBinaryCodec.js';

import type { Actor } from '../actors/Actor.js';

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

/**
 * The key used in the `state` record to store per-component JS-value snapshots.
 * Used internally for delta comparison; NOT sent on the wire.
 * Component entries are keyed by {@link SceneComponent.replicationKey}.
 */
export const REPLICATION_COMPONENTS_KEY = '$components' as const;

/**
 * Result of {@link ReplicationSerializer.serializeActor}.
 */
export interface ActorReplicationSnapshot {
  /**
   * Complete JS-value snapshot of all replicated properties (actor + components).
   * Pass this as `lastState` on the next tick for delta comparison.
   * Keyed by property name; components are under `$components.{replicationKey}`.
   */
  state: Record<string, unknown>;

  /**
   * Binary-encoded wire payload ready to send.
   * Contains either the full state (when `lastState` was null) or a delta.
   * `null` when nothing changed — do not send an update packet.
   */
  payload: Uint8Array | null;
}

// ---------------------------------------------------------------------------
// ReplicationSerializer
// ---------------------------------------------------------------------------

/**
 * Serializes and deserializes actor replicated properties.
 *
 * - `serializeActor` produces a JS-value `state` (for next-tick delta comparison) and a
 *   binary `payload` (`Uint8Array | null`) ready to send on the wire.
 * - `deserializeIntoActor` applies a binary payload to an actor and its components,
 *   capturing a JS-value snapshot of the wire state for future delta decoding.
 *
 * Per-property encoding/decoding is handled by {@link ObjectSerializer}.
 */
export class ReplicationSerializer {
  // ---------------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------------

  /**
   * Serialize an actor's replicated properties, producing both a JS-value snapshot
   * (for future delta comparison) and a binary wire payload.
   *
   * All numeric and quantized values are encoded as deltas (`new - old`). When a
   * property has no prior value in `lastState` (key absent / `undefined`), the delta
   * base is `0`, which is equivalent to sending the absolute value.
   *
   * @param actor           The actor to serialize.
   * @param lastState       The `state` snapshot from the previous call for this actor-client pair.
   * @param isOwningClient  Whether the target client is the actor's owning client.
   *                        Controls filtering of properties marked with `replicateTo: 'owner'`
   *                        or `replicateTo: 'non-owner'`.
   */
  static serializeActor(
    actor: Actor,
    lastState: Record<string, unknown>,
    isOwningClient: boolean
  ): ActorReplicationSnapshot {
    const writer = new BinaryWriter();
    const actorState: Record<string, unknown> = {};

    const lastActorState = this.withoutKeys(lastState, [REPLICATION_COMPONENTS_KEY]);

    const actorChanged = serializeObjectAndEncode(writer, actor, lastActorState, actorState, isOwningClient);

    const lastComponents = lastState[REPLICATION_COMPONENTS_KEY] as
      Record<string, Record<string, unknown>> | undefined;

    const componentsFull: Record<string, Record<string, unknown>> = {};

    // Reserve the component-count byte; patch it once we know how many components changed.
    const componentCountCursor = writer.bitCursor;
    writer.writeUint8(0);
    let changedComponentCount = 0;

    for (const [subId, component] of actor.getReplicatedComponentMap().entries()) {
      const lastComp = lastComponents?.[component.replicationKey] ?? {};
      const compState: Record<string, unknown> = {};

      const beforeCursor = writer.bitCursor;
      writer.writeUint8(subId);
      const compChanged = serializeObjectAndEncode(writer, component, lastComp, compState, isOwningClient);

      if (compChanged) {
        changedComponentCount++;
      } else {
        // Rollback sub-ID + bitmask + any values: nothing to send.
        writer.resetToBit(beforeCursor);
      }

      componentsFull[component.replicationKey] = compState;
    }

    writer.patchUint8(componentCountCursor, changedComponentCount);

    const state: Record<string, unknown> = { ...actorState };
    if (Object.keys(componentsFull).length > 0) {
      state[REPLICATION_COMPONENTS_KEY] = componentsFull;
    }

    const hasChanges = actorChanged || changedComponentCount > 0;
    return { state, payload: hasChanges ? writer.getBytes() : null };
  }

  /**
   * Deserialize a binary replication payload into an actor while simultaneously
   * capturing a JS-value snapshot of exactly what was on the wire.
   *
   * - Only properties present in the bitmask are written to the actor; unchanged
   *   properties are carried forward from `prevSnapshot` (not from the live actor,
   *   which may be locally predicted).
   * - Actor references are captured as `{ $actorRef: uuid }` in the snapshot even
   *   when the referenced actor is not yet spawned.
   * - Component snapshots absent from the payload are carried forward from
   *   `prevSnapshot.$components` unchanged.
   *
   * @param actor               Target actor.
   * @param payload             Binary wire payload from `serializeActor`.
   * @param prevSnapshot        Snapshot returned by a previous call, or the CDO snapshot for
   *                            the initial spawn.
   * @param outSnapshot         Output record that receives the updated JS-value snapshot.
   * @param resolveActorRef     Resolver for actor UUID → Actor instance.
   * @param outPendingCallbacks Collected `onRep*` callbacks to fire after all properties
   *                            are applied (e.g. after adding the actor to the world on
   *                            the spawn path, or immediately after on the update path).
   * @returns Map of dot-path → referenced actor UUID for any unresolved actor references.
   */
  static deserializeIntoActor(
    actor: Actor,
    payload: Uint8Array,
    prevSnapshot: Record<string, unknown>,
    outSnapshot: Record<string, unknown>,
    resolveActorRef: ((uuid: string) => Actor | null) | undefined,
    outPendingCallbacks: Array<[object, string]>,
  ): Map<string, string> {
    const unresolvedRefs = new Map<string, string>();
    const reader = new BinaryReader(payload);

    const prevActorState = this.withoutKeys(prevSnapshot, [REPLICATION_COMPONENTS_KEY]);

    decodeObjectFromBinary(
      reader, actor, prevActorState, outSnapshot, resolveActorRef, '', unresolvedRefs, outPendingCallbacks,
    );

    const componentCount = reader.readUint8();
    const componentMap = actor.getReplicatedComponentMap();
    const prevComponents = prevSnapshot[REPLICATION_COMPONENTS_KEY] as
      Record<string, Record<string, unknown>> | undefined;
    const componentSnapshots: Record<string, Record<string, unknown>> = {};

    for (let i = 0; i < componentCount; i++) {
      const subId = reader.readUint8();
      const component = componentMap[subId];
      if (component) {
        const prevComp = prevComponents?.[component.replicationKey] ?? {};
        const compOut: Record<string, unknown> = {};
        decodeObjectFromBinary(
          reader, component, prevComp, compOut,
          resolveActorRef, `${REPLICATION_COMPONENTS_KEY}.${component.replicationKey}`, unresolvedRefs, outPendingCallbacks,
        );
        componentSnapshots[component.replicationKey] = compOut;
      } else {
        throw new Error(
          `[ReplicationSerializer] Unknown component sub-ID ${subId} in replication payload. ` +
          'Packet is corrupt or component replication keys have changed.'
        );
      }
    }

    // Carry forward unchanged component snapshots from prevSnapshot.
    if (prevComponents) {
      for (const [repKey, prevCompState] of Object.entries(prevComponents)) {
        if (!componentSnapshots[repKey]) componentSnapshots[repKey] = prevCompState;
      }
    }

    if (Object.keys(componentSnapshots).length > 0) {
      outSnapshot[REPLICATION_COMPONENTS_KEY] = componentSnapshots;
    }

    return unresolvedRefs;
  }

  // ---------------------------------------------------------------------------
  // CDO snapshot helpers
  // ---------------------------------------------------------------------------

  /**
   * Returns a JS-value snapshot of `actor`'s replicated properties at their
   * class-default values, using the same per-property encoding as `serializeActor`.
   *
   * Pass the result as `lastState` / `prevSnapshot` on the initial spawn so that
   * only properties that differ from their defaults are included in `initialState`,
   * instead of sending every replicated property unconditionally.
   *
   * Results are cached per `(actor.constructor, isOwner)` — computed at most twice
   * per class (once for owner, once for non-owner).
   *
   * @returns The CDO snapshot, or `null` if class defaults cannot be computed.
   *   Callers should fall back to a full-state spawn when `null` is returned.
   */
  static getCdoSnapshot(actor: Actor, isOwner: boolean): Record<string, unknown> | null {
    const idx = isOwner ? 1 : 0;
    let entry = this.cdoSnapshotCache.get(actor.constructor);
    if (entry && entry[idx] !== undefined) return entry[idx] ?? null;

    if (!entry) {
      entry = [undefined, undefined];
      this.cdoSnapshotCache.set(actor.constructor, entry);
    }

    let snapshot: Record<string, unknown> | null = null;
    try {
      const cdoActor = getDefaultObject(actor);
      if (cdoActor) {
        const actorState: Record<string, unknown> = {};
        serializeObjectToState(cdoActor, actorState, isOwner);

        const componentsFull: Record<string, Record<string, unknown>> = {};
        for (const component of actor.getReplicatedComponentMap()) {
          const cdoComp = getDefaultObject(component);
          if (!cdoComp) continue;
          const compState: Record<string, unknown> = {};
          serializeObjectToState(cdoComp, compState, isOwner);
          componentsFull[component.replicationKey] = compState;
        }

        snapshot = { ...actorState };
        if (Object.keys(componentsFull).length > 0) {
          snapshot[REPLICATION_COMPONENTS_KEY] = componentsFull;
        }
      }
    } catch (err) {
      console.warn(
        `[ReplicationSerializer] Failed to compute CDO snapshot for ${actor.constructor.name}, ` +
        'falling back to full-state spawn:',
        err,
      );
    }

    entry[idx] = snapshot;
    return snapshot;
  }

  /**
   * Cache of CDO snapshots keyed by actor constructor.
   * Each entry is a 2-tuple: index 0 = non-owner snapshot, index 1 = owner snapshot.
   * `undefined` means not yet computed; `null` means CDO was unavailable.
   */
  private static cdoSnapshotCache = new Map<
    Function,
    [Record<string, unknown> | null | undefined, Record<string, unknown> | null | undefined]
  >();

  // ---------------------------------------------------------------------------
  // Private helpers
  // ---------------------------------------------------------------------------

  private static withoutKeys(
    obj: Record<string, unknown>,
    keys: string[]
  ): Record<string, unknown> {
    const copy = { ...obj };
    for (const key of keys) delete copy[key];
    return copy;
  }
}
